import { useCallback, useEffect, useRef } from 'react';

import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';

const DEFAULT_EXPORT_FORMAT = 'image/png';
const DEFAULT_EXPORT_QUALITY = 1;

export default function useImageManipulator() {
  const imageNode = useRef<HTMLImageElement | null>(null);
  const canvasNode = useRef<HTMLCanvasElement | null>(null);

  const loadImage = useCallback((imageUrl: string): Promise<HTMLImageElement> => {
    if (imageNode.current) {
      imageNode.current = null;
    }

    const image = new Image();
    image.crossOrigin = ATTACHMENT_IMAGE_CROSS_ORIGIN;
    image.src = imageUrl;
    imageNode.current = image;

    return new Promise((resolve, reject) => {
      image.addEventListener('error', () => reject(new Error('FAILED_TO_LOAD_IMAGE')));
      image.addEventListener('load', () => resolve(image));
    });
  }, []);

  const rotateAndExportImage = useCallback(
    (
      image: HTMLImageElement,
      exportSettings: {
        exportFileName: string;
        exportFormat?: string;
        exportQuality?: number;
      },
      rotationSettings: {
        angleInDegrees?: number;
        direction?: 'clockwise' | 'counterclockwise';
      } = {},
    ): Promise<{ url: string; file: File }> => {
      const {
        exportFileName,
        exportFormat = DEFAULT_EXPORT_FORMAT,
        exportQuality = DEFAULT_EXPORT_QUALITY,
      } = exportSettings;
      const { angleInDegrees = 90, direction = 'counterclockwise' } = rotationSettings;

      if (canvasNode.current) {
        canvasNode.current = null;
      }

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalHeight;
      canvas.height = image.naturalWidth;
      canvasNode.current = canvas;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return Promise.reject(new Error('FAILED_TO_GET_CANVAS_CONTEXT'));
      }

      ctx.translate(canvas.width / 2, canvas.height / 2);

      const angle = direction === 'clockwise' ? angleInDegrees : -1 * angleInDegrees;
      ctx.rotate((angle * Math.PI) / 180);

      ctx.drawImage(image, -1 * (image.naturalWidth / 2), -1 * (image.naturalHeight / 2));

      const url = canvas.toDataURL(exportFormat, exportQuality);

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], exportFileName, { type: blob.type });
              resolve({ url, file });
            } else {
              reject(new Error('FAILED_TO_CREATE_BLOB'));
            }
          },
          exportFormat,
          exportQuality,
        );
      });
    },
    [],
  );

  useEffect(() => {
    return () => {
      imageNode.current = null;
      canvasNode.current = null;
    };
  }, []);

  return { loadImage, rotateAndExportImage };
}
